import type { AppearanceSettings } from '@/state/appearance-settings';
import type { SubscriptionCode } from '@/state/subscription-codes';
import type {
  Subscription,
  SubscriptionGroup,
} from '@/state/subscription-groups';
import type { Tenant } from '@/state/tenant';
import { format } from 'date-fns';

import { SUPPORT_EMAIL } from '@/lib/env';
import {
  calculateNextPaymentDate,
  type ChargeEveryInterval,
  formatCycles,
  formatInterval,
  formatIntervalForTerms,
  formatMoney,
  formatPeriodLength,
  formatPrice,
  formatRecurringPrice,
} from '@/lib/utils';
import { formatClassCount } from './credits';

// A plan the studio sells for one payment: the backend ends it when that first
// period does, so nothing about it may read as recurring.
export const isSinglePayment = (
  plan: Pick<Subscription, 'number_of_billing_cycles'> | null | undefined
): boolean => plan?.number_of_billing_cycles === 1;

export const hasPaymentCounter = (
  plan: Pick<Subscription, 'number_of_billing_cycles'>
): boolean => plan.number_of_billing_cycles != null && !isSinglePayment(plan);

export const getSinglePaymentInfo = (
  payToday: number,
  currencyCode: string | null | undefined,
  count: number,
  interval: ChargeEveryInterval | string,
  paidByGiftCard = false
): string => {
  const paymentText =
    payToday > 0
      ? `One-time payment of ${formatMoney(payToday, currencyCode)}.`
      : paidByGiftCard
        ? 'Your gift card pays today.'
        : 'Nothing to pay.';
  const endDate = calculateNextPaymentDate(count, interval);
  return `${paymentText} Your membership is active for ${formatPeriodLength(
    count,
    interval
  )}, until ${format(endDate, 'MMM d, yyyy')}.`;
};

const COUNTER_UNITS: Record<string, string> = {
  WEEK: 'Week',
  MONTH: 'Month',
  YEAR: 'Year',
};

// A fixed-term plan counts its payments ("Month 3 of 3"). One payment buys one
// week, month or year, so that unit names it; any other period (every 2 weeks,
// a quarter) is counted as what it is, a payment.
export const billingCounterUnit = (
  plan: Pick<Subscription, 'charge_every_count' | 'charge_every_interval'>
): string =>
  (plan.charge_every_count || 1) === 1
    ? (COUNTER_UNITS[plan.charge_every_interval.toUpperCase()] ?? 'Payment')
    : 'Payment';

export const billingCounterTooltip = (
  plan: Pick<
    Subscription,
    'charge_every_count' | 'charge_every_interval' | 'number_of_billing_cycles'
  >
): string =>
  `${formatCycles(plan.number_of_billing_cycles!)}, billed ${formatIntervalForTerms(
    plan.charge_every_count || 1,
    plan.charge_every_interval
  )}. Your membership ends automatically after the last one.`;

type PaidCyclesCode = Pick<SubscriptionCode, 'cycles_consumed'> & {
  subscription?: Pick<Subscription, 'number_of_billing_cycles'> | null;
};

// The plan's last payment is in: the backend has already set the membership to
// end, so it reads as running out on schedule, not as cancelled.
export const hasPaidLastCycle = (code: PaidCyclesCode): boolean => {
  const cycles = code.subscription?.number_of_billing_cycles;
  return !!cycles && code.cycles_consumed >= cycles;
};

// Order receipts from an older backend omit the flag. Missing reads as
// Stripe-backed, so those receipts keep the wording they had.
type StripeFlag = Partial<Pick<SubscriptionCode, 'has_stripe_subscription'>>;

export type SubscriptionEndFields = PaidCyclesCode &
  StripeFlag &
  Pick<
    SubscriptionCode,
    'canceled_at' | 'status' | 'current_period_end' | 'type'
  >;

// Once the last cycle is in, nothing starts another: Stripe holds the end of a
// capped plan, and a code with no Stripe subscription (a free plan, one the
// studio added) ends with that cycle without a cancel ever being stamped,
// unless it is paused, which holds it where it is.
export const isEndingSubscription = (
  code: PaidCyclesCode &
    Pick<SubscriptionCode, 'canceled_at' | 'status'> &
    StripeFlag
): boolean =>
  hasPaidLastCycle(code) &&
  (!!code.canceled_at ||
    (code.has_stripe_subscription === false && code.status !== 'PAUSED'));

// Ending as sold with its paid period still running. Once the period is over,
// the cancel it carries reads as canceled again.
export const isEndingThisPeriod = (
  code: Parameters<typeof isEndingSubscription>[0] &
    Pick<SubscriptionCode, 'current_period_end'>,
  now: Date
): boolean =>
  isEndingSubscription(code) &&
  !!code.current_period_end &&
  new Date(code.current_period_end) > now;

// Bought for nothing: no Stripe subscription, and the backend's renewal job
// only takes codes the studio added, so no new period ever starts.
export const isNeverRenewed = (
  code: Pick<SubscriptionCode, 'type'> & StripeFlag
): boolean => code.type === 'PAID' && code.has_stripe_subscription === false;

type ClassesTermPlan = Pick<
  Subscription,
  | 'number_of_billing_cycles'
  | 'renewal_classes_behavior'
  | 'charge_every_count'
  | 'charge_every_interval'
>;

export const planClassesLabel = (classes: number | null | undefined): string =>
  classes == null ? 'Unlimited' : formatClassCount(classes);

// What a plan's classes cover: how long its one payment lasts ("2 weeks"),
// all of it when every credit comes with the first payment ("(upfront)"), or
// each billing period ("/mo").
export const planClassesTerm = (
  plan: ClassesTermPlan,
  options?: { shorten?: boolean }
): string => {
  const count = plan.charge_every_count || 1;
  if (isSinglePayment(plan)) {
    return formatPeriodLength(count, plan.charge_every_interval);
  }
  if (plan.renewal_classes_behavior === 'LEAVE_AS_IS') return '(upfront)';
  return formatInterval(count, plan.charge_every_interval, options);
};

export const withPlanClassesTerm = (
  label: string,
  plan: ClassesTermPlan
): string => {
  const term = planClassesTerm(plan);
  if (isSinglePayment(plan)) return `${label} for ${term}`;
  return plan.renewal_classes_behavior === 'LEAVE_AS_IS'
    ? `${label} ${term}`
    : `${label}${term}`;
};

export const getAllSubscriptions = (
  groups: SubscriptionGroup[]
): Subscription[] => {
  return [...groups]
    .sort((a, b) => a.order - b.order)
    .reduce((allSubs, group) => {
      const sortedSubs = [...(group.subscriptions || [])].sort(
        (a, b) => (a.order ?? Infinity) - (b.order ?? Infinity)
      );
      return [...allSubs, ...sortedSubs];
    }, [] as Subscription[]);
};

// The public endpoint only returns ACTIVE, PUBLIC, non-custom subscriptions,
// so presence is the whole test.
export const hasPublicSubscriptions = (
  groups: SubscriptionGroup[] | undefined
): boolean => !!groups?.some((g) => !!g.subscriptions?.length);

export const getAdminAddedMessage = (tenant: Tenant | undefined) =>
  tenant?.name
    ? `This membership was added to you by ${tenant.name}. Contact ${tenant.support_email ?? SUPPORT_EMAIL} for assistance.`
    : null;

export const isAdminAddedSubscription = (
  order:
    | Pick<
        SubscriptionCode['order'],
        'coupon_type' | 'final_amount' | 'admin_user_id'
      >
    | undefined
) =>
  order?.coupon_type === 'FREE' &&
  order?.final_amount === 0 &&
  order?.admin_user_id !== null;

// What the customer already pays, not the studio's shelf price — Stripe keeps
// billing the old amount after a catalog edit, so an empty catalog price here
// means unknown, not free. Null tells the caller to omit the price rather than
// assert one; the genuinely free case is the admin-added branch above it.
export const getOwnedSubscriptionPriceText = (
  subscriptionCode: SubscriptionCode
): string | null => {
  if (isAdminAddedSubscription(subscriptionCode.order)) return 'Free';
  const { subscription } = subscriptionCode;
  if (!subscription.price) return null;
  // One payment is the whole price, so a code shows what was paid, not the
  // list price. The latest order is that payment only when it billed the full
  // price (total_amount is the subtotal before any discount) or nothing, for a
  // free code. An upgrade into the plan bills just a proration, so it shows the
  // list price.
  if (isSinglePayment(subscription)) {
    const { order } = subscriptionCode;
    // A money card's share is a payment and final_amount is only Stripe's part
    // (gift cards M1, D306), so the order's total is what was paid.
    if (order.gift_card_paid)
      return formatMoney(order.total_amount, order.currency);
    return order.total_amount === subscription.price || order.total_amount === 0
      ? formatPrice(order.final_amount, order.currency)
      : formatPrice(subscription.price, subscription.currency);
  }
  return formatRecurringPrice(
    subscription.price,
    subscription.currency,
    subscription.charge_every_count,
    subscription.charge_every_interval
  );
};

export const findBillingSubscription = (
  codes: SubscriptionCode[] | undefined
) =>
  (codes ?? []).find(
    (c) => c.has_stripe_subscription && !isAdminAddedSubscription(c.order)
  );

// Mirrors the backend's resume-charge test, which is narrower than "lapsed":
// only a Stripe-backed code is charged at all, so a studio-comped membership
// resumes for free however long it sat paused. Its paid-through date is
// current_period_end falling back to expires_at — a code sold with a term but
// no cycle end is past due on that date, and reading current_period_end alone
// would let the charge land with no confirmation.
export const willResumeRecharge = (
  code: Pick<
    SubscriptionCode,
    'status' | 'has_stripe_subscription' | 'current_period_end' | 'expires_at'
  >,
  now: Date = new Date()
): boolean => {
  if (code.status !== 'PAUSED' || !code.has_stripe_subscription) return false;
  const cutoff = code.current_period_end || code.expires_at;
  return !!cutoff && new Date(cutoff) <= now;
};

export const subscriptionCardFlagsOf = (
  settings: AppearanceSettings['subscription'] | undefined
) => ({
  showImage: !!(settings?.show_image_in_list ?? settings?.show_image),
  showPrice: !!(settings?.show_price_in_list ?? settings?.show_price),
  showDescription: settings?.show_description_in_list ?? true,
});
